import { pdfRenditionFilename } from 'features/knowledgeDocumentViewer/utils/documentPageImages'
import { extractAndDownloadPages } from 'features/knowledgeDocumentViewer/utils/downloadDocumentPages'
import type { PdfOutlineUnit } from 'features/knowledgeDocumentViewer/utils/pdfOutlineUnits'
import { useState } from 'react'
import { downloadFromUrl } from 'utils/download'

const DOWNLOAD_ERROR_LABELS = {
  FULL_PDF: 'Failed to download document:',
  ORIGINAL: 'Failed to download original document:',
  CURRENT_PAGE: 'Failed to download current page:',
  PAGE_RANGE: 'Failed to download page range:',
  OUTLINE_UNIT: 'Failed to download outline unit:',
}

type UseDocumentDownloadActionsArgs = {
  pdfSignedUrl: string | undefined
  originalSignedUrl: string | undefined
  documentFilename: string | undefined
  currentPageNumber: number
}

export function useDocumentDownloadActions({
  pdfSignedUrl,
  originalSignedUrl,
  documentFilename,
  currentPageNumber,
}: UseDocumentDownloadActionsArgs) {
  const [isDownloading, setIsDownloading] = useState(false)

  const runDownload = async (
    action: () => Promise<void>,
    errorLabel: string
  ) => {
    setIsDownloading(true)
    try {
      await action()
    } catch (error) {
      console.error(errorLabel, error)
    } finally {
      setIsDownloading(false)
    }
  }

  const handleFullPdfDownload = async () => {
    if (!pdfSignedUrl || !documentFilename) return
    await runDownload(
      () =>
        downloadFromUrl(pdfSignedUrl, pdfRenditionFilename(documentFilename)),
      DOWNLOAD_ERROR_LABELS.FULL_PDF
    )
  }

  const handleOriginalDownload = async () => {
    if (!originalSignedUrl || !documentFilename) return
    await runDownload(
      () => downloadFromUrl(originalSignedUrl, documentFilename),
      DOWNLOAD_ERROR_LABELS.ORIGINAL
    )
  }

  const handleCurrentPageDownload = async () => {
    if (!pdfSignedUrl || !documentFilename) return
    await runDownload(
      () =>
        extractAndDownloadPages(
          pdfSignedUrl,
          documentFilename,
          currentPageNumber,
          currentPageNumber
        ),
      DOWNLOAD_ERROR_LABELS.CURRENT_PAGE
    )
  }

  const handleRangeDownload = async (startPage: number, endPage: number) => {
    if (!pdfSignedUrl || !documentFilename) return
    await runDownload(
      () =>
        extractAndDownloadPages(
          pdfSignedUrl,
          documentFilename,
          startPage,
          endPage
        ),
      DOWNLOAD_ERROR_LABELS.PAGE_RANGE
    )
  }

  const handleOutlineUnitDownload = async (unit: PdfOutlineUnit) => {
    if (!pdfSignedUrl || !documentFilename) return
    await runDownload(
      () =>
        extractAndDownloadPages(
          pdfSignedUrl,
          documentFilename,
          unit.startPage,
          unit.endPage
        ),
      DOWNLOAD_ERROR_LABELS.OUTLINE_UNIT
    )
  }

  return {
    isDownloading,
    handleFullPdfDownload,
    handleOriginalDownload,
    handleCurrentPageDownload,
    handleRangeDownload,
    handleOutlineUnitDownload,
  }
}
